import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, formatApiError } from '@/lib/AuthContext';
import { Lock, Shield, AlertTriangle, Check } from 'lucide-react';

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, changePassword } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await changePassword(newPassword);
      navigate(user?.is_platform_admin ? '/admin' : '/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const checks = [
    { ok: newPassword.length >= 8, label: 'At least 8 characters' },
    { ok: /[A-Z]/.test(newPassword), label: 'One uppercase letter' },
    { ok: /[0-9]/.test(newPassword), label: 'One number' },
    { ok: /[^A-Za-z0-9]/.test(newPassword), label: 'One special character' },
  ];

  return (
    <div className="min-h-screen bg-[#0A2533] flex items-center justify-center px-4" data-testid="change-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-white font-bold text-2xl">Nuvanta Solutions</span>
            <span className="text-[#4DD0E1]">|</span>
            <span className="text-[#4DD0E1] font-semibold text-2xl">TradePath</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-5 w-5 text-[#FF6B35]" />
            <h1 className="text-xl font-bold text-[#0A2533]">Change your password</h1>
          </div>
          <p className="text-sm text-[#4E6974] mb-6">You must set a new password before continuing.</p>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm rounded-lg p-3 mb-4" data-testid="change-password-error">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#4E6974] mb-1.5">New password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" />
                <input data-testid="new-password-input" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#0A2533] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none" required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#4E6974] mb-1.5">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" />
                <input data-testid="confirm-password-input" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#0A2533] focus:border-[#0097A7] focus:ring-1 focus:ring-[#0097A7] outline-none" required />
              </div>
            </div>

            <div className="space-y-1">
              {checks.map((c, i) => (
                <div key={i} className={`flex items-center gap-2 text-xs ${c.ok ? 'text-green-600' : 'text-[#B0BEC5]'}`}>
                  <Check className="h-3 w-3" /> {c.label}
                </div>
              ))}
            </div>

            <button data-testid="change-password-submit" type="submit" disabled={loading}
              className="w-full py-2.5 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white font-medium text-sm rounded-lg transition-colors">
              {loading ? 'Updating...' : 'Set new password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
